import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useLeague } from '@/contexts/LeagueContext';

// SAB-575: MLB same-sport league switcher, extracted from the inline block in
// MLBBeta so it can render at BOTH breakpoints (centered on mobile, left-of-brand
// on desktop) from one source. Behavior parity with the NFL LeagueSwitcher:
// >1 league → dropdown of all the user's MLB leagues, click sets the active one;
// ≤1 → plain league name. `userLeagues` is fetched once by MLBBeta and passed in
// so the two rendered instances don't double-fetch.
export function MLBLeagueSwitcher({
  userLeagues,
  onLeagueAction,
}: {
  userLeagues: { id: string; name: string }[];
  // SAB-592: open the shell's create/join view. When absent the actions are hidden.
  onLeagueAction?: (action: 'create' | 'join') => void;
}) {
  const { activeLeague, setActiveLeagueId } = useLeague();
  const [open, setOpen] = useState(false);

  // SAB-592: always a dropdown (even for one league) so Join + Create are reachable
  // for a user who already has a league. The old ≤1 branch showed a bare name.
  return (
    <div className="relative">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 px-3 py-1 rounded-lg text-sm font-semibold text-foreground hover:bg-muted/50 transition-colors max-w-[200px]"
      >
        <span className="truncate">{activeLeague?.name}</span>
        <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 z-50 w-48 rounded-lg border border-border bg-background shadow-xl py-1">
            {userLeagues.map((league) => (
              <button
                key={league.id}
                onClick={() => {
                  setActiveLeagueId(league.id);
                  setOpen(false);
                }}
                className={`w-full text-left px-3 py-2 text-sm transition-colors truncate ${
                  league.id === activeLeague?.id
                    ? 'text-primary font-semibold bg-primary/10'
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                }`}
              >
                {league.name}
              </button>
            ))}
            {onLeagueAction && (
              <>
                <div className="my-1 h-px bg-border" />
                <button
                  onClick={() => { onLeagueAction('join'); setOpen(false); }}
                  className="w-full text-left px-3 py-2 text-sm font-semibold text-primary hover:bg-muted transition-colors"
                >
                  + Join a league
                </button>
                <button
                  onClick={() => { onLeagueAction('create'); setOpen(false); }}
                  className="w-full text-left px-3 py-2 text-sm font-semibold text-primary hover:bg-muted transition-colors"
                >
                  + Create a league
                </button>
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
}
